import { put, call, all, takeLatest } from 'redux-saga/effects';
import i18next from 'i18next';

import {
  del,
  get,
  patch,
  post,
} from '../services/api';
import {
  changeNameFailed,
  changeNameSuccess,
  createFolderFailed,
  createFolderSuccess,
  deleteFailed,
  deleteSuccess,
  errorViewFile,
  filesDownloaded,
  filesMarkedAsRead,
  filesMoved,
  filesUploaded,
  fileToUploadUpdated,
  folderLoaded,
  folderMoved,
  folderStructureLoaded,
  loadFolderFailed,
  markFilesAsReadAttempted,
  moveFilesFailed,
  recentFilesLoaded,
  recentFilesLoadFailed,
  recentFilesRequested,
  showedViewFile,
  tagPetFileFailed,
  tagPetFileSuccess,
  telusDocsFailed,
  telusDocsLoaded,
  uploadFiles,
  getTelusDocs as GET_TELUS_DOCS,
  dialogMessageUpdated,
} from '../actions';
import {
  base64toBlob,
  delay,
  downloadFileInPage,
  objectToPascalCase,
} from '../services/utils';
import {
  usePetCloudCustomerV1T176448ForSagas,
} from '../services/featureFlagsForSagas';
import {
  EDUCATION_MATERIAL_FOLDER_ID,
  EDUCATION_MATERIAL_STORE_KEY,
  READ_FILE_SOURCE,
} from '../components/inboxAndDocs/inboxAndDocs.constants';
import { getEducationMaterialFolderContent } from '../services/inboxAndDocs';

export function* loadFolderContent(dispatch, { payload: folderId }) {
  const url = `customer/folders/${folderId}`;

  const educationMaterialContent = getEducationMaterialFolderContent(folderId);
  const isEducationMaterialFolder = String(folderId)
    === String(EDUCATION_MATERIAL_FOLDER_ID);

  // if educational material retrieved from local storage
  // and is not education material folder (root) (refreshes local storage on
  if (educationMaterialContent && !isEducationMaterialFolder) {
    yield put(folderLoaded(educationMaterialContent));
    return;
  }

  // no education material found, so we ask the backend for the content as usual
  const response = yield call(
    get,
    dispatch,
    url,
  );

  if (response.IsValid || response.success) {
    const responseData = response.Data || objectToPascalCase(response.data);
    yield put(folderLoaded(responseData));

    // if education material folder was requested
    // we save its content in local storage for future use
    if (isEducationMaterialFolder) {
      localStorage.setItem(
        EDUCATION_MATERIAL_STORE_KEY,
        JSON.stringify(responseData),
      );
    }
  } else {
    yield put(loadFolderFailed(response.Message || ''));
  }
}

export function* getRecentFiles(dispatch) {
  yield put(recentFilesRequested());
  const url = 'customer/files';

  const response = yield call(
    get,
    dispatch,
    url,
  );

  if (response.IsValid || response.success) {
    const responseData = response.Data || objectToPascalCase(response.data);
    yield put(recentFilesLoaded(responseData));
  } else {
    yield put(recentFilesLoadFailed(response.Message));
  }
}

export function* markFilesAsRead(dispatch, { payload }) {
  const url = 'customer/files/batch';
  const { filesIds, source } = payload;

  if (source === READ_FILE_SOURCE.toolbar) {
    yield put(markFilesAsReadAttempted());
  }

  yield call(
    patch,
    dispatch,
    url,
    filesIds.map((fileId) => ({
      id: fileId,
      isRead: true,
    })),
  );

  if (source !== READ_FILE_SOURCE.claims) {
    yield put(filesMarkedAsRead(filesIds));
  }
}

export function* moveFiles(dispatch, { payload }) {
  const { destinationFolderId, filesIds } = payload;
  const response = yield call(
    post,
    dispatch,
    'customer/InboxDocs/MoveFiles',
    {
      FileIds: filesIds,
      MoveFolderId: destinationFolderId,
    },
  );

  if (response.IsValid) {
    yield put(filesMoved());
  } else {
    yield put(moveFilesFailed());
  }
}

export function* moveFolder(dispatch, { payload }) {
  const url = 'customer/folders';
  const { destinationFolderId, folderIdToMove } = payload;

  const request = {
    folderId: folderIdToMove,
    parentFolderId: destinationFolderId,
  };

  const response = yield call(
    patch,
    dispatch,
    url,
    request,
  );

  if (response.success) {
    yield put(folderMoved());
  } else {
    yield put(dialogMessageUpdated({ visible: true }));
  }
}

export function* loadFolderStructure(dispatch) {
  const url = 'customer/folders';

  const response = yield call(
    get,
    dispatch,
    url,
  );

  if (response.IsValid || response.success) {
    const responseData = objectToPascalCase(response.data);
    yield put(folderStructureLoaded(responseData));
  }
}

export function* changeName(dispatch, { payload }) {
  const { isFolder, itemId, newName } = payload;
  /* TODO: RenameFiles v2 not ready */
  let url = 'customer/InboxDocs/RenameFile';
  let method = post;

  let request = {
    FileId: itemId,
    NewFileName: newName.trim(),
  };

  if (isFolder) {
    url = 'customer/folders';
    method = patch;
    request = {
      FolderId: itemId,
      FolderName: newName.trim(),
    };
  }

  const response = yield call(
    method,
    dispatch,
    url,
    request,
  );

  if (response.IsValid || response.success) {
    yield put(changeNameSuccess());
  } else {
    const errorMessage = response.Message || response.error.detail;
    yield put(changeNameFailed(errorMessage || 'Unexpected error.'));
  }
}

function* downloadMedia(dispatch, resourceId) {
  const url = 'customer/files/zip';
  const data = { fileIds: [resourceId], folderIds: [] };
  const endpointResponse = yield call(
    post,
    dispatch,
    url,
    data,
    'json',
    false,
  );

  if (endpointResponse.success) {
    const { fileContents, contentType } = endpointResponse;
    const blob = base64toBlob(fileContents, contentType);
    const blobUrl = URL.createObjectURL(blob);

    return {
      Data: { contentType, fileContents, MediaUrl: blobUrl },
      IsValid: true,
    };
  }

  return {
    Data: null,
    IsValid: false,
    Message: endpointResponse.error,
  };
}

export function* openMediaFileToBlob(dispatch, { payload }) {
  let response = {};

  try {
    response = yield downloadMedia(dispatch, payload);

    if (response.IsValid) {
      window.open(response.Data.MediaUrl, '_blank');
    } else {
      yield put(dialogMessageUpdated({ visible: true }));
    }
  } catch (_) {
    yield put(dialogMessageUpdated({ visible: true }));
  }

  return response;
}

export function* downloadFiles(dispatch, { payload }) {
  const {
    singleFile = false,
    singleFileInfo = {},
    fileIds,
    folderIds,
  } = payload;

  const timestamp = new Date().toISOString().split('T')[0];
  const defaultZipName = `MyPetCloud_download_${timestamp}.zip`;

  const usePetCloudCustomerV1T176448 =
    yield usePetCloudCustomerV1T176448ForSagas();

  if (singleFile && !usePetCloudCustomerV1T176448) {
    const { Id } = singleFileInfo;
    const responseUrlBlob = yield downloadMedia(dispatch, Id);

    if (responseUrlBlob.IsValid) {
      yield downloadFileInPage({
        fileName: singleFileInfo.Name,
        fileUrl: responseUrlBlob.Data.MediaUrl,
      });
    } else {
      yield put(dialogMessageUpdated({ visible: true }));
    }

    yield put(filesDownloaded());
    return;
  }

  const url = 'customer/files/zip';
  const request = {
    fileIds: singleFile ? [singleFileInfo?.Id] : fileIds,
    folderIds: singleFile ? []
      : folderIds.map((id) => parseInt(id, 10)),
  };

  const response = yield call(
    post,
    dispatch,
    url,
    request,
  );

  if (response.success) {
    const zipName = response?.fileDownloadName || defaultZipName;
    const base64Data = response?.fileContents;

    downloadFileInPage({ base64Data, fileName: zipName });
  } else {
    yield put(dialogMessageUpdated({ visible: true }));
  }

  yield put(filesDownloaded());
}

export function* tagPetFile(dispatch, { payload }) {
  const url = 'customer/files';

  const { currentFolderId, fileId, petIdList } = payload;

  const request = {
    folderId: currentFolderId,
    id: fileId,
    petIds: petIdList,
  };

  const response = yield call(
    patch,
    dispatch,
    url,
    request,
  );

  if (response.success || (response.IsValid && response.Data)) {
    yield put(tagPetFileSuccess());
  } else {
    yield put(tagPetFileFailed(response.Message || 'Unexpected error.'));
  }
}

export function* createFolder(dispatch, { payload }) {
  const { parentId, name } = payload;
  const data = { folderName: name, parentFolderId: parentId };
  const url = 'customer/folders';

  const response = yield call(
    post,
    dispatch,
    url,
    data,
  );

  if (response.IsValid || response.success) {
    yield put(createFolderSuccess());
  } else {
    const errorMessage = response.Message || response.error.detail;
    yield put(createFolderFailed(errorMessage || 'Unexpected error.'));
  }
}

export function* deleteFiles(dispatch, { payload }) {
  const { fileIds } = payload;

  const url = `customer/files/${encodeURI(fileIds.join())}`;

  const response = yield call(
    del,
    dispatch,
    url,
    {},
  );

  if (response.IsValid || response.success) {
    yield put(deleteSuccess());
  } else {
    yield put(deleteFailed(response.Message || 'Unexpected error.'));
  }
}

export function* deleteFolder(dispatch, { payload }) {
  const { folderId } = payload;

  const url = `customer/folders/${folderId}`;

  const response = yield call(
    del,
    dispatch,
    url,
  );

  if (response.IsValid || response.success) {
    yield put(deleteSuccess());
  } else {
    yield put(deleteFailed(response.Message || 'Unexpected error.'));
  }
}

function* simulateLoading({ fileKey, progress }) {
  if (progress < 70) {
    yield call(delay, 50);
    yield put(fileToUploadUpdated({
      fileKey,
      newValues: {
        errorMsg: '',
        percentage: progress,
      },
    }));
    yield call(simulateLoading, {
      fileKey,
      progress: progress + 10,
    });
  }
}

function* uploadFile({ dispatch, fileKey, request }) {
  const url = 'customer/files';
  const response = yield call(
    post,
    dispatch,
    url,
    request,
  );
  yield call(simulateLoading, { fileKey, progress: 1 });
  if (response.IsValid || response.success) {
    yield put(fileToUploadUpdated({
      fileKey,
      newValues: {
        hideDelete: true,
        percentage: 100,
      },
    }));
  } else {
    yield put(fileToUploadUpdated({
      fileKey,
      newValues: {
        valid: false,
      },
    }));
  }
}

export function* uploadFilesPerBatch(dispatch, { payload }) {
  const { files, orderIds, folderId, batchSize, currentBatch } = payload;
  if (files.length) {
    const batch = files.slice(0, batchSize);
    yield all(batch.map((file) => {
      const request = {
        ContentType: file.contentType,
        Extension: file.extension,
        FileContent: file.binary,
        FileName: file.filename,
        FolderId: folderId,
        OrderIds: orderIds,
      };
      return call(uploadFile, {
        dispatch,
        fileKey: file.key,
        request,
      });
    }));
    const nextBatch = files.slice(batchSize);
    yield put(uploadFiles({
      batchSize,
      currentBatch: currentBatch + 1,
      files: nextBatch,
      folderId,
      orderIds,
    }));
  } else {
    yield put(filesUploaded());
  }
}

export function* loadBlobFile(dispatch, { payload }) {
  const { Id } = payload;
  const response = yield downloadMedia(dispatch, Id);

  if (response.IsValid) {
    const { MediaUrl } = response.Data;

    yield put(showedViewFile({
      ...payload,
      Metadata: {
        ...payload.Metadata,
        Url: MediaUrl,
      },
    }));
  } else {
    yield put(errorViewFile());
    yield put(dialogMessageUpdated({
      message: i18next.t('inboxAndDocs:errorModal.message'),
      title: i18next.t('inboxAndDocs:errorModal.title'),
      visible: true,
    }));
  }
}

export function* getTelusDocs(dispatch) {
  const url = 'customer/InboxDocs/telus/document';
  const params = { brand: 'PPU' };

  const response = yield call(
    get,
    dispatch,
    url,
    params,
  );

  if (response.IsValid || response.success) {
    yield put(telusDocsLoaded(response.blobPath));
  } else {
    yield put(telusDocsFailed());
  }
}

// This is the main saga that will be exported
export function* inboxAndDocsSaga(store) {
  yield all([
    takeLatest(GET_TELUS_DOCS, getTelusDocs, store.dispatch),
  ]);
}
